import React from "react";
import { Link } from "react-router-dom";
import PageHero from "../components/PageHero";
import { CheckCircle2, Globe, Award, Users, Target, Heart } from "lucide-react";
import { EditableText, EditableImage } from "../components/live/Editable";

export default function AboutPage() {
  const values = [
    { icon: Target, title: "Excellence", desc: "International-standard curriculum delivered with German precision." },
    { icon: Heart, title: "Care", desc: "Every student matters — personal attention from faculty and mentors." },
    { icon: Globe, title: "Global Outlook", desc: "German certification opens doors to international careers." },
    { icon: Award, title: "Industry-Ready", desc: "Hands-on training that prepares you for the real factory floor." },
  ];

  return (
    <>
      <PageHero
        path="about.hero"
        kicker="About Us"
        title="About Kiki Centre for Technology"
        subtitle="Skilling India's youth as per international standards with German precision and Indian heart."
      />

      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div className="grid grid-cols-2 gap-4">
            <EditableImage path="about.story.img1" src="https://images.unsplash.com/photo-1597058557804-95ac4ee36e66?w=600" className="rounded-2xl shadow-lg w-full h-64 overflow-hidden" imgClassName="w-full h-full object-cover" />
            <EditableImage path="about.story.img2" src="https://images.unsplash.com/photo-1690356107685-3725367f6f3f?w=600" className="rounded-2xl shadow-lg w-full h-64 overflow-hidden mt-8" imgClassName="w-full h-full object-cover" />
            <EditableImage path="about.story.img3" src="https://images.unsplash.com/photo-1716191299980-a6e8827ba10b?w=600" className="rounded-2xl shadow-lg w-full h-64 overflow-hidden -mt-4" imgClassName="w-full h-full object-cover" />
            <EditableImage path="about.story.img4" src="https://images.unsplash.com/photo-1740209475472-aa7d280f7452?w=600" className="rounded-2xl shadow-lg w-full h-64 overflow-hidden mt-4" imgClassName="w-full h-full object-cover" />
          </div>
          <div>
            <EditableText path="about.story.kicker" as="p" className="kicker mb-3">Our Story</EditableText>
            <EditableText path="about.story.heading" as="h2" className="text-3xl font-bold text-[#0B2A6B] mb-5">
              Building India's technical workforce, one student at a time
            </EditableText>
            <EditableText path="about.story.para1" as="p" multiline className="text-slate-600 leading-relaxed mb-4">
              Established with the support of a World Bank REFLOWS programme, the German Chamber of Skilled Crafts (HWK), Koblenz, Germany and Indian industry, Anjali Foundation — under the brand name of Kiki Centre for Technology — has established a high-quality, international-standard technical training institute at IMT Manesar, Gurgaon, Delhi-NCR. The institute focuses on developing skills with the support of German experts.
            </EditableText>
            <EditableText path="about.story.para2" as="p" multiline className="text-slate-600 leading-relaxed mb-6">
              A range of courses are conducted — from long-term 3.5-year and 2.5-year programmes to short-term practical courses and customised corporate training — all delivered in a 25,000 sq ft facility with world-class machinery and didactical equipment from Germany, India, Japan and the USA, and a curriculum developed by world-class German experts alongside highly experienced Indian faculty.
            </EditableText>
            <ul className="space-y-2.5">
              {[
                { k: "b1", t: "German HWK Koblenz Certified" },
                { k: "b2", t: "20+ years of training excellence" },
                { k: "b3", t: "95%+ placement track record" },
                { k: "b4", t: "Partner to 60+ leading companies" },
              ].map((it) => (
                <li key={it.k} className="flex items-center gap-2 text-slate-700">
                  <CheckCircle2 className="h-5 w-5 text-yellow-500" />
                  <EditableText path={`about.story.bullet.${it.k}`} as="span">{it.t}</EditableText>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Courses conducted */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="kicker mb-3">Programmes</p>
            <h2 className="text-3xl font-bold text-[#0B2A6B]">Various courses are conducted</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {[
              "Long term 3.5-year courses for students who are class 10 or class 12 pass (all streams)",
              "Long term 2.5-year courses for ITI, Diploma or B.Tech graduates (mechanical, electrical, electronics & allied streams)",
              "Short term practical-oriented courses from 1–11 months for Diploma, ITI, Degree and B.Tech students",
              "Short term customised corporate courses ranging from 1 day to 3 months",
            ].map((c) => (
              <div key={c} className="flex items-start gap-3 bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
                <CheckCircle2 className="h-5 w-5 text-yellow-500 mt-0.5 shrink-0" />
                <p className="text-slate-700 text-sm leading-relaxed">{c}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Institute features */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="kicker mb-3">The Institute</p>
            <h2 className="text-3xl font-bold text-[#0B2A6B]">Features of the institute</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              "25,000 sq ft technical training institute",
              "Well-equipped classrooms with computers and projectors",
              "International-standard laboratory with learning software and simulators",
              "High-technology industrial workshops",
              "World-class machinery from Germany, India, Japan and USA",
              "Curriculum by German experts & trained Indian faculty",
            ].map((f) => (
              <div key={f} className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm tilt-card">
                <p className="text-slate-700 text-sm leading-relaxed">{f}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Awards */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="kicker mb-3">Recognition</p>
            <h2 className="text-3xl font-bold text-[#0B2A6B]">Our students win against the best</h2>
            <p className="text-slate-600 mt-3 max-w-2xl mx-auto text-sm">
              Our students compete in national and regional technical competitions against other institutes and even companies like Maruti and Tata Motors — and win.
            </p>
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl p-7 border border-slate-100 shadow-sm">
              <h3 className="font-bold text-[#0B2A6B] text-lg mb-4 flex items-center gap-2"><Award className="h-5 w-5 text-yellow-500" />National Awards</h3>
              <ul className="space-y-2.5">
                {[
                  "Gold — National Work Skills Competition 2025 (Tool & Die Making)",
                  "Gold — National Work Skills Competition 2025 (Turner - Mechanical)",
                  "Gold — National Work Skills Competition 2024 (Plastic Die Engineering)",
                  "Gold — National Work Skills Competition 2024 (Tool & Die Making)",
                  "Gold — National Work Skills Competition 2017 & 2014 (Tool & Die Making)",
                  "Silver — National Work Skills Competition 2024 (Fitter - Mechanical)",
                  "Silver — National Work Skills Competition 2023 (Milling - Mechanical)",
                  "Medallion of Excellence — India Skills 2024 (Manufacturing Team Challenge)",
                ].map((a) => (
                  <li key={a} className="flex items-start gap-2 text-sm text-slate-600">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-yellow-400 shrink-0" />{a}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-7 border border-slate-100 shadow-sm">
              <h3 className="font-bold text-[#0B2A6B] text-lg mb-4 flex items-center gap-2"><Award className="h-5 w-5 text-yellow-500" />Regional & State Awards</h3>
              <ul className="space-y-2.5 max-h-72 overflow-y-auto pr-2">
                {[
                  "Winner — Haryana State IndiaSkills, Mechanical Design 2026",
                  "Winner — Haryana State IndiaSkills, Manufacturing Team Challenge 2024",
                  "Gold — Regional Work Skills North 2025 (CNC Turner, Plastic Die, Tool & Die, AutoCAD, PLC, Pneumatics, Mechatronics, Electrical)",
                  "Gold — Regional Work Skills North 2024 (Plastic Die, Tool & Die, Fitter, Milling, Turner, Mechatronics)",
                  "Gold — Regional Work Skills North 2023 (Plastic Die, Tool & Die, Turner, Miller, Fitter)",
                  "Gold — Regional Work Skills North 2022 (PLC, Machinist)",
                  "Gold — Regional Work Skills North 2015–2017 (Fitter, Tool & Die, Electronics)",
                  "Silver — Regional Work Skills North 2025 (Fitter, Welder, Tool & Die, AutoCAD, Machinist, Mechatronics, Turner)",
                  "Silver — Regional Work Skills North 2024 (Electrical, Machinist, Tool & Die)",
                  "Bronze — Regional Work Skills North 2025 (Electrical, Miller-Machinist, Welding)",
                ].map((a) => (
                  <li key={a} className="flex items-start gap-2 text-sm text-slate-600">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-slate-300 shrink-0" />{a}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Clients */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="kicker mb-3">Trusted by industry</p>
          <h2 className="text-3xl font-bold text-[#0B2A6B] mb-8">Some of our clients</h2>
          <div className="flex flex-wrap justify-center gap-3">
            {["Maruti Suzuki", "Stryker (Fortune 500)", "Becton Dickinson", "Subros", "Denso", "Asahi Glass", "IFB", "Boston Scientific"].map((c) => (
              <span key={c} className="px-4 py-2 rounded-full bg-white border border-slate-200 shadow-sm text-sm font-semibold text-[#0B2A6B]">{c}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="kicker mb-3">Our Values</p>
            <h2 className="text-3xl font-bold text-[#0B2A6B]">What we stand for</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <div
                key={v.title}
                className="bg-white rounded-2xl p-7 shadow-sm border border-slate-100 tilt-card animate-fade-up"
                style={{ animationDelay: `${i * 0.08}s` }}
              >
                <div className="h-12 w-12 rounded-xl bg-yellow-400 flex items-center justify-center mb-4">
                  <v.icon className="h-6 w-6 text-[#0B2A6B]" />
                </div>
                <h3 className="font-bold text-[#0B2A6B] text-lg mb-2">{v.title}</h3>
                <p className="text-sm text-slate-600">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-[#0B2A6B] mb-4">Ready to start your journey?</h2>
          <p className="text-slate-600 mb-7">Join thousands of students who are building careers with Kiki Centre.</p>
          <Link to="/admissions" className="inline-flex items-center px-7 py-3 rounded-lg btn-primary">Apply for Admission</Link>
        </div>
      </section>
    </>
  );
}
